<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import { useBrowserDownload } from '@peanuds/composables';
import { useCustomerConfig } from '@peanuds/app-shell';
import { useUserAccountsStore } from '@peanuds/shared-state';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { computed, ref, watch } from 'vue';

import {
  DEFAULT_AMOUNT_LOCALE,
  formatDateValue,
  hasApiErrorCode,
  isDateOnlyValue,
  normalizeIban,
  parseDateValue,
} from '@peanuds/shared';
import {
  accountsEuroIcon as EuroIcon,
  accountsGbrIcon as GbrIcon,
  UiDialogCloseButton,
  UiDialogShell,
  UiCopyToggleButton,
  accountsUsdIcon as UsdIcon,
} from '@peanuds/ui';

import { useTransactionDetailsDialogData } from '../internal/useTransactionDetailsDialogData';
import { useTransactionDetailsSelection } from '../internal/useTransactionDetailsSelection';
import CancelPaymentConfirmDialog from './CancelPaymentConfirmDialog.vue';
import type {
  TransactionDetailsDialogData,
  TransactionDetailsDialogDirection,
  TransactionDetailsDialogSource,
  TransactionDetailsDialogStatusSeverity,
} from '../transaction-details.types';

interface Props {
  visible: boolean;
  transactionId?: string | null;
  direction?: TransactionDetailsDialogDirection | null;
  transactionSource?: TransactionDetailsDialogSource | null;
}

interface Emits {
  'update:visible': [value: boolean];
  close: [];
}

type FooterPrimaryActionSlotProps = {
  dialogData: TransactionDetailsDialogData | null;
  close: () => void;
};

const {
  visible,
  transactionId = null,
  direction = null,
  transactionSource = null,
} = defineProps<Props>();
const emit = defineEmits<Emits>();
const { dateTime } = useCustomerConfig();
defineSlots<{
  'footer-message'?: () => unknown;
  'footer-primary-action'?: (props: FooterPrimaryActionSlotProps) => unknown;
}>();

const { t } = useTranslation();
const userAccountsStore = useUserAccountsStore();
const { triggerBrowserDownload } = useBrowserDownload();

const { resolvedDirection, resolvedTransactionId, activeSelection } =
  useTransactionDetailsSelection({
    visible: () => visible,
    transactionId: () => transactionId,
    direction: () => direction,
    transactionSource: () => transactionSource,
  });

const {
  cancelError,
  cancelPayment,
  dialogData,
  downloadStatement,
  error: detailError,
  isCancellingPayment,
  isLoading,
  isDownloadingStatement,
} = useTransactionDetailsDialogData({
  selection: activeSelection,
});

const isCancelConfirmVisible = ref(false);
const hasCancelPaymentFailed = ref(false);

const stateMessage = computed(() => {
  if (!resolvedTransactionId.value || !resolvedDirection.value) {
    return t('transactionDetails.states.unavailable');
  }
  if (detailError.value) return t('transactionDetails.states.error');

  return t('transactionDetails.states.empty');
});

const handleClose = () => {
  emit('update:visible', false);
  emit('close');
};

const canDownloadStatement = computed(() => {
  return Boolean(resolvedTransactionId.value && resolvedDirection.value && !isLoading.value);
});

const handleDownloadStatement = async () => {
  const statementFile = await downloadStatement();

  if (!statementFile) return;

  triggerBrowserDownload(
    statementFile.blob,
    friendlyStatementFileName.value || statementFile.fileName,
  );
};

const canCancelPayment = computed(() => {
  if (isLoading.value) return false;

  return dialogData.value?.isCancellable === true;
});

const cancelPaymentErrorMessage = computed(() => {
  if (!hasCancelPaymentFailed.value) return '';

  return hasApiErrorCode(cancelError.value, 'OUTGOING_TRANSFERS.NOT_CANCELLABLE')
    ? t('transactionDetails.actions.cancelPaymentNotEligible')
    : t('transactionDetails.actions.cancelPaymentFailed');
});

const handleCancelPaymentRequest = () => {
  hasCancelPaymentFailed.value = false;
  isCancelConfirmVisible.value = true;
};

const handleCancelPaymentConfirm = async () => {
  const selection = activeSelection.value;
  const isCancelled = await cancelPayment();

  if (activeSelection.value !== selection) return;

  hasCancelPaymentFailed.value = !isCancelled;
};

watch(activeSelection, () => {
  isCancelConfirmVisible.value = false;
  hasCancelPaymentFailed.value = false;
});

const summaryName = computed(() => {
  return dialogData.value?.summaryName?.trim() || dialogData.value?.beneficiaryName?.trim() || '-';
});

const summaryType = computed(() => {
  return dialogData.value?.summaryType?.trim() || '-';
});

const currencyIndicatorIcon = computed(() => {
  const currency = dialogData.value?.currency || 'USD';
  switch (currency) {
    case 'EUR':
      return EuroIcon;
    case 'USD':
      return UsdIcon;
    case 'GBP':
      return GbrIcon;
    default:
      return EuroIcon;
  }
});

const getNumericValue = (value: number | string | null | undefined): number | null => {
  if (value === undefined || value === null) return null;

  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatCurrencyAmount = (
  value: number | string | null | undefined,
  currency: string | null | undefined,
): string => {
  const numericValue = getNumericValue(value);
  if (numericValue === null) return '-';

  const resolvedCurrency = currency || 'USD';
  const absoluteValue = Math.abs(numericValue);

  try {
    return new Intl.NumberFormat(DEFAULT_AMOUNT_LOCALE, {
      style: 'currency',
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absoluteValue);
  } catch {
    return absoluteValue.toFixed(2);
  }
};

const formatAmountNoSign = (tx: TransactionDetailsDialogData | null): string => {
  if (!tx) return '-';

  return formatCurrencyAmount(tx.amount, tx.currency);
};

const formatFeeNoSign = (tx: TransactionDetailsDialogData | null): string => {
  if (!tx) return '-';

  return formatCurrencyAmount(tx.fee, tx.feeCurrency || tx.currency);
};

const formatLongDateTime = (tx: TransactionDetailsDialogData | null): string => {
  const candidate = tx?.dateTime;

  if (!candidate) return '-';

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  };

  try {
    const datePart = formatDateValue(candidate, dateOptions, dateTime.value.locale);

    if (!datePart) return '-';

    if (isDateOnlyValue(candidate)) return datePart;

    const timePart = formatDateValue(
      candidate,
      { hour: '2-digit', minute: '2-digit' },
      dateTime.value.locale,
    );

    if (!timePart) return '-';

    return `${datePart} ${t('transactionDetails.fields.at')} ${timePart}`;
  } catch {
    const fallback = parseDateValue(candidate);

    if (!fallback) return '-';

    return isDateOnlyValue(candidate)
      ? fallback.toLocaleDateString(dateTime.value.locale)
      : fallback.toLocaleString(dateTime.value.locale);
  }
};

const formattedDateTime = computed(() => formatLongDateTime(dialogData.value));
const formattedAmount = computed(() => formatAmountNoSign(dialogData.value));
const formattedFee = computed(() => formatFeeNoSign(dialogData.value));

watch(
  () => dialogData.value?.fromAccountId,
  async (fromAccountId) => {
    if (!fromAccountId) return;
    if (userAccountsStore.getAccountById(fromAccountId)) return;

    await userAccountsStore.ensureAccountsLoaded();
  },
  { immediate: true },
);

const truncateIban = (value: string): string => {
  const compact = value.replace(/\s+/g, '');
  if (compact.length <= 24) return compact;

  return `${compact.slice(0, 16)}..${compact.slice(-4)}`;
};

const sourceAccount = computed(() => {
  const fromAccountId = dialogData.value?.fromAccountId?.trim();
  if (!fromAccountId) return undefined;

  return userAccountsStore.getAccountById(fromAccountId);
});

const fromAccountName = computed(() => {
  return (
    dialogData.value?.fromAccountName?.trim() || sourceAccount.value?.friendlyName?.trim() || ''
  );
});

const fromAccountTagLabel = computed(() => {
  const name = fromAccountName.value.trim();
  if (!name) return '';

  return `${dialogData.value?.currency || sourceAccount.value?.currency || 'USD'} ${name}`;
});

const fromAccountIbanRaw = computed(() => {
  const sourceAccountIban =
    typeof sourceAccount.value?.iban === 'string' ? sourceAccount.value.iban.trim() : '';

  return dialogData.value?.fromAccountIban?.trim() || sourceAccountIban || '';
});

const fromAccountIban = computed(() => {
  const raw = fromAccountIbanRaw.value;
  return raw ? truncateIban(raw) : '-';
});

const hasFromAccountDetails = computed(() => {
  return Boolean(fromAccountTagLabel.value || fromAccountIbanRaw.value);
});

const beneficiaryDisplayName = computed(() => {
  return dialogData.value?.beneficiaryName?.trim() || dialogData.value?.summaryName?.trim() || '-';
});

const transactionIdentifier = computed(() => {
  return dialogData.value?.transactionNumber?.trim() || '-';
});

const bankNameAddress = computed(() => {
  return dialogData.value?.bankNameAddress?.trim() || '-';
});

const reference = computed(() => {
  return dialogData.value?.reference?.trim() || '-';
});

const toFileNameSegment = (value: string | null | undefined): string => {
  const normalized = value?.trim();
  if (!normalized || normalized === '-') return '';

  return normalized
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
};

const getStatementDateSegment = (value: string | null | undefined): string => {
  if (!value || value === '-') return '';

  const parsedDate = parseDateValue(value);
  if (!parsedDate) return '';

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const friendlyStatementFileName = computed(() => {
  const direction = resolvedDirection.value;
  const transactionIdSegment = toFileNameSegment(transactionIdentifier.value);

  if (!direction) {
    return transactionIdSegment ? `transaction-confirmation_${transactionIdSegment}.pdf` : '';
  }

  const counterparty =
    direction === 'out'
      ? beneficiaryDisplayName.value
      : summaryName.value || beneficiaryDisplayName.value;
  const counterpartySegment = toFileNameSegment(counterparty);
  const dateSegment = getStatementDateSegment(dialogData.value?.dateTime);
  const prefix = direction === 'out' ? 'payment-confirmation' : 'payment-receipt';
  const segments = [prefix, counterpartySegment, dateSegment].filter(Boolean);

  if (segments.length > 1) return `${segments.join('_')}.pdf`;

  return transactionIdSegment
    ? `transaction-confirmation_${transactionIdSegment}.pdf`
    : `${prefix}.pdf`;
});

const statusTag = computed(() => {
  const statusLabelKey = dialogData.value?.statusLabelKey?.trim();
  const fallbackLabel =
    (statusLabelKey ? t(statusLabelKey) : dialogData.value?.statusLabel?.trim()) ||
    t('transactionDetails.status.pending');
  const fallbackSeverity: TransactionDetailsDialogStatusSeverity =
    dialogData.value?.statusSeverity ?? 'secondary';

  return {
    value: fallbackLabel,
    severity: fallbackSeverity,
  };
});
</script>

<template>
  <UiDialogShell
    :visible="visible"
    width="26.75rem"
    max-width="90vw"
    :dismissable-mask="false"
    :show-pattern="false"
    surface="plain"
    labelled-by="transaction-details-dialog-title"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #surface>
      <div class="transaction-details" data-testid="transaction-details-dialog">
        <UiDialogCloseButton label="Close" @click="handleClose" />

        <div class="transaction-details__content dots-pattern-3">
          <div class="transaction-details__header">
            <h3 id="transaction-details-dialog-title" class="transaction-details__title">
              {{ t('transactionDetails.title') }}
            </h3>
            <p class="transaction-details__subtitle">{{ t('transactionDetails.subtitle') }}</p>
          </div>

          <div class="transaction-details__body">
            <div
              v-if="isLoading"
              class="transaction-details__state transaction-details__state--loading"
            >
              <ProgressSpinner
                class="transaction-details__spinner"
                strokeWidth="6"
                animationDuration="1s"
                aria-label="Loading transaction details"
              />
              <p class="transaction-details__state-message">
                {{ t('transactionDetails.states.loading') }}
              </p>
            </div>

            <div v-else-if="dialogData" class="transaction-details__details-content">
              <div class="transaction-details__summary">
                <div class="transaction-details__summary-left">
                  <div class="transaction-details__summary-icon" aria-hidden="true">
                    <i v-if="resolvedDirection === 'out'" class="pi pi-arrow-up-right" />
                    <i v-else class="pi pi-arrow-down-left" />
                  </div>
                  <p
                    class="transaction-details__summary-name"
                    data-testid="transaction-details-summary-name"
                  >
                    {{ summaryName }}
                  </p>
                </div>
                <p class="transaction-details__summary-type">{{ summaryType }}</p>
              </div>

              <div class="transaction-details__rows">
                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.dateTime') }}
                    </p>
                    <p class="transaction-details__value">
                      {{ formattedDateTime }}
                    </p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.dateTime')"
                    :copy-text="formattedDateTime"
                    :disabled="formattedDateTime === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.status') }}
                    </p>
                    <div class="transaction-details__value">
                      <Tag class="transaction-details__status-tag" v-bind="statusTag" />
                    </div>
                  </div>
                </div>

                <div v-if="hasFromAccountDetails" class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.sentFrom') }}
                    </p>
                    <div class="transaction-details__sent-from">
                      <div class="transaction-details__sent-from-left">
                        <div class="transaction-details__currency-indicator" aria-hidden="true">
                          <img
                            :src="currencyIndicatorIcon"
                            alt=""
                            class="transaction-details__currency-icon"
                          />
                        </div>
                        <span v-if="fromAccountTagLabel" class="transaction-details__account-tag">
                          {{ fromAccountTagLabel }}
                        </span>
                      </div>
                      <span class="transaction-details__value-inline">{{ fromAccountIban }}</span>
                    </div>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.sentFrom')"
                    :copy-text="normalizeIban(fromAccountIbanRaw)"
                    :disabled="!fromAccountIbanRaw"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.amount') }}
                    </p>
                    <p class="transaction-details__value">
                      {{ formattedAmount }}
                    </p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.amount')"
                    :copy-text="formattedAmount"
                    :disabled="formattedAmount === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.fee') }}
                    </p>
                    <p class="transaction-details__value">
                      {{ formattedFee }}
                    </p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.fee')"
                    :copy-text="formattedFee"
                    :disabled="formattedFee === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.beneficiary') }}
                    </p>
                    <p class="transaction-details__value">{{ beneficiaryDisplayName }}</p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.beneficiary')"
                    :copy-text="beneficiaryDisplayName"
                    :disabled="beneficiaryDisplayName === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.transactionId') }}
                    </p>
                    <p class="transaction-details__value">{{ transactionIdentifier }}</p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.transactionId')"
                    :copy-text="transactionIdentifier"
                    :disabled="transactionIdentifier === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.bankNameAddress') }}
                    </p>
                    <p class="transaction-details__value">{{ bankNameAddress }}</p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.bankNameAddress')"
                    :copy-text="bankNameAddress"
                    :disabled="bankNameAddress === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>

                <div class="transaction-details__row transaction-details__row--last">
                  <div class="transaction-details__row-main">
                    <p class="transaction-details__label">
                      {{ t('transactionDetails.fields.reference') }}
                    </p>
                    <p class="transaction-details__value transaction-details__value--multiline">
                      {{ reference }}
                    </p>
                  </div>
                  <UiCopyToggleButton
                    button-size="2.375rem"
                    icon-size="1rem"
                    icon-dark
                    icon-hover-background="none"
                    :tooltip-text="t('transactionDetails.fields.reference')"
                    :copy-text="reference"
                    :disabled="reference === '-'"
                    class="transaction-details__copy-btn"
                  />
                </div>
              </div>
            </div>

            <div v-else class="transaction-details__state">
              <p class="transaction-details__state-message">{{ stateMessage }}</p>
            </div>
          </div>

          <Message
            v-if="cancelPaymentErrorMessage"
            severity="error"
            size="small"
            data-testid="transaction-details-cancel-payment-error"
            class="transaction-details__footer-message"
          >
            {{ cancelPaymentErrorMessage }}
          </Message>

          <slot name="footer-message" />

          <div class="transaction-details__footer">
            <Button
              v-if="canCancelPayment"
              severity="danger"
              rounded
              class="transaction-details__footer-btn"
              data-testid="transaction-details-cancel-payment"
              :label="
                isCancellingPayment
                  ? t('transactionDetails.actions.cancellingPayment')
                  : t('transactionDetails.actions.cancelPayment')
              "
              :disabled="isCancellingPayment"
              :loading="isCancellingPayment"
              @click="handleCancelPaymentRequest"
            />
            <Button
              v-else
              severity="secondary"
              rounded
              class="transaction-details__footer-btn"
              data-testid="transaction-details-download-statement"
              :label="
                isDownloadingStatement
                  ? t('transactionDetails.actions.downloadingStatement')
                  : t('transactionDetails.actions.downloadStatement')
              "
              :disabled="!canDownloadStatement || isDownloadingStatement"
              :loading="isDownloadingStatement"
              @click="handleDownloadStatement"
            />
            <slot name="footer-primary-action" :dialog-data="dialogData" :close="handleClose">
              <Button
                severity="primary"
                rounded
                class="transaction-details__footer-btn"
                :label="t('transactionDetails.actions.close')"
                @click="handleClose"
              />
            </slot>
          </div>
        </div>
      </div>
    </template>
  </UiDialogShell>

  <CancelPaymentConfirmDialog
    v-model:visible="isCancelConfirmVisible"
    @confirm="handleCancelPaymentConfirm"
  />
</template>

<style lang="scss" scoped>
.transaction-details {
  display: flex;
  flex-direction: column;
  position: relative;
  height: 550px;
  max-height: calc(100dvh - #{$static-space-6}); // 24px
  overflow: hidden;

  &__content {
    display: flex;
    flex-direction: column;
    gap: $static-space-5; // 20px (Figma)
    padding: $space-lg $space-lg $space-lg;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: $static-space-5; // 20px (Figma)
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  &__details-content {
    display: flex;
    flex-direction: column;
    gap: $static-space-5; // 20px (Figma)
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: $static-space-2; // 8px
    align-items: center;
    text-align: center;
  }

  &__title {
    font-size: f(24, 24);
    font-weight: $font-semibold;
    line-height: 1.3;
    letter-spacing: -0.096px; // from Figma
    color: var(--text-color, #334155);
    margin: 0;
  }

  &__subtitle {
    font-size: $static-text-sm;
    font-weight: $font-regular;
    line-height: $leading-none;
    letter-spacing: -0.056px; // from Figma
    color: var(--text-color, #64748b);
    margin: 0;
    max-width: 21.375rem; // 342px
  }

  &__state {
    display: flex;
    flex: 1;
    min-height: 14rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: $space-lg;
    border-radius: var(--p-border-radius-xxl, 20px);
    background: var(--p-surface-50, #f8fafc);
  }

  &__state--loading {
    flex-direction: column;
    gap: $space-md;
  }

  &__state-message {
    margin: 0;
    font-size: $static-text-md;
    font-weight: $font-medium;
    color: var(--text-color, #64748b);
  }

  &__spinner {
    width: 2.5rem;
    height: 2.5rem;
  }

  &__summary {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $static-space-2; // 8px
    background: var(--p-surface-50, #f8fafc);
    border-radius: f(32, 32); // 32px from Figma
    padding: $static-space-3 $static-space-4; // 12px 16px-ish
  }

  &__summary-left {
    display: flex;
    align-items: center;
    gap: $static-space-2; // 8px
    min-width: 0;
  }

  &__summary-icon {
    width: f(24, 24);
    height: f(24, 24);
    border-radius: var(--p-border-radius-lg);
    background: var(--p-surface-200, #e2e8f0);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      color: var(--p-surface-700, #334155);
      font-size: f(16, 16);
    }
  }

  &__summary-name {
    margin: 0;
    font-size: $static-text-md;
    font-weight: $font-regular;
    color: var(--p-surface-700, #334155);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__summary-type {
    margin: 0;
    font-size: $static-text-sm;
    font-weight: $font-semibold;
    color: var(--p-surface-700, #334155);
    text-align: right;
    white-space: nowrap;
  }

  &__rows {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: $static-space-5; // 20px
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $static-space-2; // 8px
    padding: 0 $static-space-3 $static-space-2; // 0 12px 8px
    border-bottom: 1px solid #e0f2fe; // from Figma (outlined info active background)
  }

  &__row--last {
    border-bottom: none;
  }

  &__row-main {
    display: flex;
    flex-direction: column;
    gap: $static-space-2; // 8px
    min-width: 0;
    flex: 1;
  }

  &__label {
    margin: 0;
    font-size: $static-text-md;
    font-weight: $font-medium;
    line-height: $leading-none;
    color: var(--text-color, #64748b);
    letter-spacing: -0.064px;
  }

  &__value {
    margin: 0;
    font-size: $static-text-md;
    font-weight: $font-semibold;
    line-height: $leading-tight;
    color: var(--text-color, #334155);
    letter-spacing: 0.15px; // from Figma
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__value--multiline {
    white-space: normal;
  }

  &__copy-btn {
    flex-shrink: 0;
  }

  &__status-tag {
    border-radius: var(--p-border-radius-lg);
    font-weight: $font-semibold;
  }

  &__returned-tag {
    border-radius: var(--p-border-radius-lg);
    font-weight: $font-semibold;
    margin-left: $static-space-2;
  }

  &__sent-from {
    display: flex;
    align-items: center;
    gap: $static-space-2; // 8px
    min-width: 0;
  }

  &__sent-from-left {
    display: flex;
    align-items: center;
    gap: $static-space-1; // 4px (Figma)
    flex-shrink: 0;
  }

  &__currency-indicator {
    width: 30px; // static from design (match iban-swiper)
    height: 30px; // static from design (match iban-swiper)
    border-radius: 46px; // fully rounded (match iban-swiper)
    display: flex;
    justify-content: center;
    overflow: clip; // Figma: overflow clipped
    flex-shrink: 0;
    position: relative;
    align-items: center;
    font-weight: $font-semibold;
    color: var(--p-surface-700, #334155);
    background: var(--p-surface-0, #ffffff);
  }

  &__currency-icon {
    width: 100%;
    height: 100%;
    display: block;
  }

  &__account-tag {
    background-color: var(--tag-secondary-background, #f1f5f9);
    border-radius: $radius-xl; // 12px (match iban-swiper)
    padding: 3.5px 7px; // hardcoded to match Figma (token has halves)
    font-size: $text-xs; // 12px (match iban-swiper)
    font-weight: $font-semibold;
    line-height: normal;
    letter-spacing: -0.048px;
    color: var(--tag-primary-color, #0c7ed7);
    white-space: nowrap;
  }

  &__value-inline {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__attachments {
    width: 100%;
    padding: $static-space-4 $static-space-3 0; // 16px 12px 0
    display: flex;
    flex-direction: column;
    gap: $static-space-4; // 16px
  }

  &__label--attachments {
    color: var(--text-color, #64748b);
  }

  &__attachment-card {
    background: var(--p-surface-0, #ffffff);
    border: 1px solid var(--p-surface-200, #e2e8f0);
    border-radius: var(--p-border-radius-lg);
    padding: $space-md;
  }

  &__attachment {
    display: flex;
    flex-direction: column;
    gap: $static-space-3; // 12px
  }

  &__attachment-info {
    display: flex;
    flex-direction: column;
    gap: $static-space-2; // 8px
  }

  &__attachment-name {
    margin: 0;
    font-size: $static-text-sm;
    font-weight: $font-medium;
    color: var(--p-surface-700, #334155);
  }

  &__attachment-size {
    margin: 0;
    font-size: $static-text-sm;
    font-weight: $font-regular;
    color: var(--p-surface-700, #334155);
  }

  &__attachment-actions {
    display: flex;
    gap: $static-space-1; // 4px
    align-items: center;
    flex-wrap: wrap;
  }

  &__file-btn {
    height: f(27, 27);
    padding: $static-space-2 $static-space-3;
    border-radius: var(--p-border-radius-absolut, 999px);
  }

  &__file-btn--filled {
    height: f(26, 26);
    background: var(--p-surface-100, #f1f5f9);
    border-color: var(--p-surface-100, #f1f5f9);
    color: var(--p-surface-600, #475569);
  }

  &__file-btn-label {
    font-size: $static-text-xs;
    font-weight: $font-medium;
    letter-spacing: -0.048px;
  }

  &__file-btn-icon {
    font-size: f(12, 12);
  }

  &__footer-message {
    flex-shrink: 0;
    border-radius: $radius-2xl;
    font-size: $text-sm;
  }

  &__footer {
    display: flex;
    gap: $static-space-2; // 8px
    width: 100%;
    padding-top: $space-md;
  }

  &__footer-btn,
  &__footer :deep(.transaction-details__footer-btn) {
    flex: 1;
    height: f(38, 38);
    border-radius: var(--p-border-radius-absolut, 999px);
    font-size: $static-text-md;
    font-weight: $font-medium;

    :deep(.p-button-label) {
      white-space: nowrap;
    }
  }
}
</style>
